import { useCallback, useEffect, useMemo, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { ReactFlowProvider } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { IHighlight } from "@financial-advisory/ui/components/pdf-viewer";
import {
  ClausePlaceholder,
  HighlightColorKeys,
  PdfViewer,
} from "@financial-advisory/ui/features/decision-tree";
import { GraphLegend } from "./GraphLegend";
import {
  LayoutFlowStatic,
  UpdatedNodeInfo,
  convertPanesToGraph,
  applyNodeUpdates,
} from "@unlikelyai-magic/shared/components";
import { PaneTypes } from "@unlikelyai-magic/shared/data-access/api";
import { cn } from "@unlikelyai-magic/utils";

export interface QuestionAnswer {
  question: string;
  value: unknown;
  reasoning?: string;
}

export type GraphViewState = "ingested" | "edit" | "final";

export interface TriStatePanes {
  ingested: PaneTypes[];
  edit: PaneTypes[];
  final: PaneTypes[];
}

export interface RegulationsViewProps {
  documentName?: string;
  highlights: IHighlight[];
  triStatePanes?: TriStatePanes;
  panes?: PaneTypes[];
  questionAnswers?: QuestionAnswer[];
  hasActiveClause?: boolean;
  /**
   * Updated nodes to show in the "edit" graph state.
   * These annotate nodes with PRE_EVALUATED, REPHRASING, or SPLIT_OUT status.
   */
  editUpdatedNodes?: UpdatedNodeInfo[];
  /** When true, the document panel is hidden and the graph fills the full width. */
  hideDocument?: boolean;
  /** Page to scroll to on initial mount (from editData). Only used once on mount. */
  initialScrollToPage?: number;
  /** Whether edit data exists for the current active clause */
  hasEditData?: boolean;
}

const NoPdfPlaceholder = () => (
  <div className="flex flex-col items-center justify-center h-full p-8 bg-gray-50">
    <div className="text-gray-400 mb-4">
      <svg
        className="w-16 h-16"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
    </div>
    <p className="text-gray-500 text-center">
      No document available for this guardrail.
    </p>
    <p className="text-gray-400 text-sm text-center mt-2">
      The graph view on the right shows the decision tree.
    </p>
  </div>
);

const ClauseRemovedPlaceholder = () => (
  <div className="flex items-center justify-center h-full w-full bg-white">
    <p className="text-black text-lg font-medium">
      This clause was detected as likely not applicable, which was then confirmed with human review.
    </p>
  </div>
);

interface GraphStateToggleProps {
  value: GraphViewState;
  onChange: (state: GraphViewState) => void;
}

const GraphStateToggle = ({ value, onChange }: GraphStateToggleProps) => {
  const states: { key: GraphViewState; label: string }[] = [
    { key: "ingested", label: "Ingested" },
    { key: "edit", label: "Edit" },
    { key: "final", label: "Final" },
  ];

  return (
    <div className="absolute top-2 right-2 z-10 bg-white rounded-[10px] p-[3px] flex gap-0 shadow-md border border-gray-200">
      {states.map(({ key, label }) => (
        <button
          key={key}
          type="button"
          onClick={() => onChange(key)}
          className={cn(
            "relative px-3 py-1 rounded-[8px] text-sm font-medium leading-6 transition-all overflow-hidden",
            value === key
              ? "text-brand-foreground"
              : "text-brand-muted hover:text-brand-foreground"
          )}
        >
          {value === key && (
            <div className="absolute inset-0 bg-brand-primary opacity-15 rounded-[8px]" />
          )}
          <span className="relative z-10">{label}</span>
        </button>
      ))}
    </div>
  );
};

export const RegulationsView = ({
  documentName,
  highlights,
  triStatePanes,
  panes,
  questionAnswers = [],
  hasActiveClause = false,
  editUpdatedNodes = [],
  hideDocument = false,
  initialScrollToPage,
  hasEditData = true,
}: RegulationsViewProps) => {
  const [graphState, setGraphState] = useState<GraphViewState>("ingested");
  const [activePaneId, setActivePaneId] = useState<string | null>(null);
  const [scrollToPage, setScrollToPage] = useState<number | undefined>(initialScrollToPage);

  // Clear scroll target after initial scroll completes
  useEffect(() => {
    if (scrollToPage !== undefined) {
      const timer = setTimeout(() => {
        setScrollToPage(undefined);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [scrollToPage]);

  // Reset graph state to "ingested" when clause changes
  useEffect(() => {
    setGraphState("ingested");
  }, [triStatePanes]);

  useEffect(() => {
    const handleError = (e: ErrorEvent) => {
      const errorMessage = e.message || e.error?.message || "";
      if (
        errorMessage.includes(
          "ResizeObserver loop completed with undelivered notifications"
        ) ||
        errorMessage.includes("ResizeObserver loop limit exceeded")
      ) {
        e.stopImmediatePropagation();
        e.preventDefault();
        return false;
      }
    };
    const handleUnhandledRejection = (e: PromiseRejectionEvent) => {
      const errorMessage = e.reason?.message || String(e.reason || "");
      if (
        errorMessage.includes(
          "ResizeObserver loop completed with undelivered notifications"
        ) ||
        errorMessage.includes("ResizeObserver loop limit exceeded")
      ) {
        e.preventDefault();
        return false;
      }
    };

    window.addEventListener("error", handleError, true);
    window.addEventListener(
      "unhandledrejection",
      handleUnhandledRejection,
      true
    );

    return () => {
      window.removeEventListener("error", handleError, true);
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection,
        true
      );
    };
  }, []);

  const activePanes = useMemo(() => {
    if (triStatePanes) {
      return triStatePanes[graphState];
    }
    return panes ?? [];
  }, [triStatePanes, panes, graphState]);

  // Pre-process graph with edit annotations when in "edit" state
  const preProcessedGraph = useMemo(() => {
    if (graphState !== "edit" || editUpdatedNodes.length === 0) {
      return undefined;
    }
    const graph = convertPanesToGraph(activePanes, questionAnswers);
    return applyNodeUpdates(graph.nodes, graph.edges, editUpdatedNodes);
  }, [graphState, activePanes, questionAnswers, editUpdatedNodes]);

  // Handle node click to set active pane and scroll to its highlights
  const handleNodeClick = useCallback((nodeId: string, nodeType: string, paneType?: any) => {
    if (nodeType === "group" && paneType) {
      setActivePaneId(nodeId);
    }
  }, []);

  const layoutFlowStatic = useMemo(() => {
    return (
      <LayoutFlowStatic
        panes={activePanes}
        questionAnswers={questionAnswers}
        preProcessedGraph={preProcessedGraph}
        onNodeClick={handleNodeClick}
      />
    );
  }, [activePanes, questionAnswers, preProcessedGraph, handleNodeClick]);

  const hasPdf = documentName && !documentName.includes("placeholder");
  const showToggle = Boolean(triStatePanes);

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-row h-full">
        {!hideDocument && (
          <div className="relative flex flex-col flex-1 w-[45%] h-full bg-gray-200 border-r border-gray-200">
            <HighlightColorKeys />
            <div className="relative flex-1">
              <ErrorBoundary fallback={<div className="p-4">Error loading PDF</div>}>
                {hasPdf ? (
                  <PdfViewer
                    documentName={documentName}
                    highlights={highlights}
                    scrollToPage={scrollToPage}
                  />
                ) : (
                  <NoPdfPlaceholder />
                )}
              </ErrorBoundary>
            </div>
          </div>
        )}
        <div className={cn(
          "flex flex-col relative h-full overflow-hidden",
          hideDocument ? "w-full" : "w-[55%]"
        )}>
          {showToggle && (
              <GraphStateToggle value={graphState} onChange={setGraphState} />
            )}
          <div className="flex-1 overflow-hidden relative">
            {!hasEditData && (graphState === "edit" || graphState === "final") ? (
              <ClauseRemovedPlaceholder />
            ) : hasActiveClause && activePanes.length > 0 ? (
              <ReactFlowProvider key={`${documentName}-${graphState}-${activePanes.map(p => p.id).join(',')}`}>
                {layoutFlowStatic}
              </ReactFlowProvider>
            ) : (
              <ClausePlaceholder />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
